import { useCallback, useEffect, useRef } from 'react'

const HOVER_INTENT_MS = 120

/**
 * Hover/focus "navigation intent" handlers: invoke `run` after a short delay,
 * cancelling on mouse-leave/blur AND on unmount (so a card that unmounts mid-hover
 * — filter change, pagination, route nav — never fires a stray prefetch after it
 * is gone). `run` is expected to be idempotent/deduped by the caller (e.g. a
 * cache-first query guarded by a seen-set).
 *
 * Shared by the per-entity prefetch hooks (material, community, …) so the timing
 * and cleanup live in one place.
 */
export function usePrefetchOnIntent(run: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Keep the latest `run` without re-creating the handlers when it changes.
  const runRef = useRef(run)
  runRef.current = run

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = undefined
    }
  }, [])

  const start = useCallback(() => {
    cancel()
    timer.current = setTimeout(() => runRef.current(), HOVER_INTENT_MS)
  }, [cancel])

  // Clear any pending timer when the consuming component unmounts.
  useEffect(() => cancel, [cancel])

  return {
    onMouseEnter: start,
    onMouseLeave: cancel,
    onFocus: start,
    onBlur: cancel,
  }
}
